import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Radar, Loader2, Trash2, PenLine, Sparkles, Compass, Activity, TrendingUp, Search, Clock, Users, Star, ChevronDown, ChevronUp, RefreshCw, ExternalLink, Instagram, Linkedin, Copy, Check, Send } from "lucide-react";

const LANES = [
  { id: "attention", label: "Command attention", icon: Sparkles, tint: "text-rose-300 bg-rose-500/15" },
  { id: "trending", label: "Trending now", icon: TrendingUp, tint: "text-amber-300 bg-amber-500/15" },
  { id: "search_led", label: "Search-led", icon: Search, tint: "text-emerald-300 bg-emerald-500/15" },
  { id: "evergreen", label: "Evergreen", icon: Clock, tint: "text-blue-300 bg-blue-500/15" },
  { id: "member_connected", label: "Member-connected", icon: Users, tint: "text-purple-300 bg-purple-500/15" },
];
const laneMeta = (id) => LANES.find((l) => l.id === id) || LANES[2];

export const TrafficEngine = () => {
  const [opps, setOpps] = useState([]);
  const [focus, setFocus] = useState("");
  const [scanning, setScanning] = useState(false);
  const [tab, setTab] = useState("feed");
  const [members, setMembers] = useState([]);
  const [syncing, setSyncing] = useState(false);

  const load = async () => setOpps(await api.trafficOpps());
  const loadMembers = async () => setMembers(await api.magazineMembers().catch(() => []));
  useEffect(() => { load(); loadMembers(); }, []);

  const syncMembers = async () => {
    setSyncing(true);
    try {
      const r = await api.magazineSync();
      setMembers(r.members || []);
      toast.success(`Synced ${r.count} live members from smemagazine.co.uk.`);
    } catch { toast.error("Couldn't reach the live magazine."); }
    finally { setSyncing(false); }
  };

  const scan = async () => {
    setScanning(true);
    try {
      await api.trafficScan(focus.trim());
      toast.message("KURT is scanning for traffic opportunities across all five lanes…");
      let n = 0;
      const before = opps.length;
      const t = setInterval(async () => {
        n++;
        const d = await api.trafficOpps();
        setOpps(d);
        if (d.length > before || n > 20) { clearInterval(t); setScanning(false); if (d.length > before) toast.success(`KURT surfaced ${d.length - before} new opportunities.`); }
      }, 5000);
    } catch { toast.error("Scan failed."); setScanning(false); }
  };

  const clearAll = async () => { await api.trafficClear(); await load(); toast.success("Cleared."); };

  const grouped = LANES.map((l) => ({ ...l, items: opps.filter((o) => o.lane === l.id) }));

  return (
    <div data-testid="screen-traffic" className="max-w-6xl mx-auto">
      <div className="kz-fade-up">
        <p className="text-sm text-emerald-400 font-semibold tracking-wide">TRAFFIC · EDITORIAL · DISCOVERY ENGINE</p>
        <h1 className="kz-head text-3xl md:text-4xl font-extrabold tracking-tight mt-2">KURT knows what S/ME should publish next</h1>
        <p className="text-slate-400 mt-2 text-sm">Always-on editorial traffic strategy — the intersection of what readers want, what's searched, what's gaining attention, what helps members, and what still feels like S/ME.</p>
      </div>

      <div data-testid="member-sync-bar" className="mt-5 flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-[#121824] px-4 py-2.5">
        <div className="flex items-center gap-2 text-sm">
          <Users size={15} className={members.length ? "text-emerald-400" : "text-slate-500"} />
          {members.length ? (
            <span className="text-slate-300"><span className="font-semibold text-emerald-400">{members.length}</span> live members synced from smemagazine.co.uk — KURT links to their real profiles</span>
          ) : (
            <span className="text-slate-400">No live members synced yet — sync so discovery pages link to real member profiles.</span>
          )}
        </div>
        <button data-testid="sync-members-btn" onClick={syncMembers} disabled={syncing}
          className="shrink-0 flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-lg bg-white/[0.06] text-slate-100 hover:bg-white/[0.1] disabled:opacity-60 transition-all">
          {syncing ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} className="text-emerald-400" />} Sync live members
        </button>
      </div>

      <div className="flex items-center gap-1 mt-6 mb-5">
        {[["feed", "Opportunity feed", Radar], ["discovery", "Business discovery", Compass], ["social", "Social studio", Instagram], ["working", "What's working", Activity]].map(([id, label, Icon]) => (
          <button key={id} data-testid={`traffic-tab-${id}`} onClick={() => setTab(id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all ${tab === id ? "text-slate-50 bg-white/[0.08]" : "text-slate-400 hover:text-slate-200"}`}>
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {tab === "feed" && (
        <>
          <div className="flex flex-col sm:flex-row gap-2">
            <input data-testid="traffic-focus-input" value={focus} onChange={(e) => setFocus(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !scanning && scan()}
              placeholder="Optional focus (e.g. 'AI for small retailers', 'autumn budget')…"
              className="flex-1 bg-[#121824] border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-emerald-500/40" />
            <button data-testid="traffic-scan-btn" onClick={scan} disabled={scanning}
              className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-500 text-[#04140d] font-semibold text-sm hover:bg-emerald-400 disabled:opacity-60 transition-all">
              {scanning ? <Loader2 size={16} className="animate-spin" /> : <Radar size={16} />} Scan for opportunities
            </button>
            {opps.length > 0 && (
              <button data-testid="traffic-clear-btn" onClick={clearAll}
                className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-white/10 text-slate-300 hover:bg-white/[0.06] text-sm transition-all"><Trash2 size={15} /> Clear</button>
            )}
          </div>

          {opps.length === 0 ? (
            <div className="mt-6 rounded-2xl border border-white/10 bg-[#121824] p-10 text-center text-sm text-slate-400">
              No opportunities yet — hit <span className="text-emerald-400 font-semibold">Scan</span> and KURT will find what S/ME should publish to win readers, searchers and buyers.
            </div>
          ) : (
            <div className="mt-6 space-y-7">
              {grouped.filter((g) => g.items.length).map((g) => (
                <div key={g.id}>
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${g.tint}`}><g.icon size={12} /> {g.label}</span>
                    <span className="text-xs text-slate-500">{g.items.length}</span>
                  </div>
                  <div className="grid md:grid-cols-2 gap-3">
                    {g.items.map((o) => <OppCard key={o.id} o={o} onChange={load} />)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {tab === "discovery" && <BusinessDiscovery />}
      {tab === "social" && <SocialStudio />}
      {tab === "working" && <WhatsWorking />}
    </div>
  );
};

const Score = ({ label, v, color }) => (
  <div className="flex-1">
    <div className="flex items-center justify-between mb-1"><span className="text-[9px] uppercase tracking-wider text-slate-500">{label}</span><span className={`text-[10px] font-bold ${color}`}>{v}</span></div>
    <div className="h-1 rounded-full bg-white/[0.06] overflow-hidden"><div className={`h-full ${color.replace("text-", "bg-")}`} style={{ width: `${v}%` }} /></div>
  </div>
);

const OppCard = ({ o, onChange }) => {
  const [busy, setBusy] = useState(false);
  const meta = laneMeta(o.lane);
  const toArticle = async () => { setBusy(true); try { await api.trafficToArticle(o.id); toast.success("Sent to the writing engine as a draft — find it in SEO Engine → Articles."); } catch { toast.error("Couldn't create the draft."); } finally { setBusy(false); } };
  const remove = async () => { setBusy(true); try { await api.trafficDelete(o.id); await onChange(); } finally { setBusy(false); } };
  return (
    <div data-testid={`traffic-opp-${o.id}`} className="rounded-2xl border border-white/10 bg-[#121824] p-4 flex flex-col">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <h3 className="kz-head text-base font-bold text-slate-100 leading-snug">{o.title}</h3>
          <p className="text-xs text-slate-400 mt-1.5">{o.angle}</p>
        </div>
        <button data-testid={`traffic-del-${o.id}`} onClick={remove} disabled={busy} className="shrink-0 text-slate-600 hover:text-red-400"><Trash2 size={13} /></button>
      </div>

      <div className="mt-3 rounded-lg bg-[#0B0F17] border border-white/[0.06] p-2.5">
        <div className="text-[9px] font-bold uppercase tracking-wider text-emerald-400 mb-1">Why it drives traffic</div>
        <p className="text-[11px] text-slate-400 leading-relaxed">{o.why_traffic}</p>
      </div>

      <div className="flex items-center gap-2 mt-3 flex-wrap">
        <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-white/5 text-slate-400">{o.suggested_section}</span>
        <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-white/5 text-slate-400">{o.format}</span>
        <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-white/5 text-slate-400">search: {o.search_demand}</span>
      </div>

      {Array.isArray(o.members_to_feature) && o.members_to_feature.length > 0 && (
        <div className="flex items-center gap-1.5 mt-2 text-[11px] text-purple-300"><Star size={11} /> {o.members_to_feature.join(", ")}</div>
      )}

      <div className="flex gap-3 mt-3">
        <Score label="Traffic" v={o.traffic_potential} color="text-emerald-400" />
        <Score label="Brand-fit" v={o.brand_fit} color="text-rose-400" />
        <Score label="Member" v={o.member_benefit} color="text-purple-400" />
      </div>

      <button data-testid={`traffic-write-${o.id}`} onClick={toArticle} disabled={busy}
        className="mt-4 flex items-center justify-center gap-1.5 text-[11px] font-semibold px-3 py-2 rounded-lg bg-white/[0.06] text-slate-100 hover:bg-white/[0.1] disabled:opacity-60 transition-all">
        {busy ? <Loader2 size={13} className="animate-spin" /> : <PenLine size={13} className="text-emerald-400" />} Write this as an article
      </button>
    </div>
  );
};

const BusinessDiscovery = () => {
  const [pages, setPages] = useState([]);
  const [service, setService] = useState("");
  const [location, setLocation] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => setPages(await api.discoveryList());
  useEffect(() => { load(); }, []);

  const draft = async () => {
    if (!service.trim()) { toast.error("Enter a service buyers search for."); return; }
    setBusy(true);
    try {
      await api.discoveryDraft(service.trim(), location.trim());
      setService(""); setLocation("");
      toast.message("KURT is drafting a glossy discovery page…");
      let n = 0;
      const t = setInterval(async () => { n++; await load(); if (n > 15) { clearInterval(t); setBusy(false); } }, 4000);
    } catch { toast.error("Draft failed."); setBusy(false); }
  };

  return (
    <div>
      <p className="text-slate-400 text-sm mb-4">Turn buyer searches (e.g. "accountants in Essex") into glossy S/ME editorial pages that route people to your members — utility on the inside, editorial on the outside.</p>
      <div className="flex flex-col sm:flex-row gap-2 mb-5">
        <input data-testid="discovery-service-input" value={service} onChange={(e) => setService(e.target.value)} placeholder="Service buyers search (e.g. photographer, accountant)…"
          className="flex-1 bg-[#121824] border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-emerald-500/40" />
        <input data-testid="discovery-location-input" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Location (optional)"
          className="sm:w-48 bg-[#121824] border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-emerald-500/40" />
        <button data-testid="discovery-draft-btn" onClick={draft} disabled={busy}
          className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-500 text-[#04140d] font-semibold text-sm hover:bg-emerald-400 disabled:opacity-60 transition-all">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Compass size={16} />} Draft discovery page
        </button>
      </div>
      {pages.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-[#121824] p-8 text-center text-sm text-slate-400">No discovery pages yet.</div>
      ) : (
        <div className="space-y-2">{pages.map((p) => <DiscoveryRow key={p.id} p={p} onChange={load} />)}</div>
      )}
    </div>
  );
};

const DiscoveryRow = ({ p, onChange }) => {
  const [open, setOpen] = useState(false);
  const remove = async () => { await api.discoveryDelete(p.id); await onChange(); };
  return (
    <div data-testid={`discovery-${p.id}`} className="rounded-xl border border-white/10 bg-[#121824] p-3">
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-slate-100 truncate">{p.drafting ? "Drafting discovery page…" : p.title}</span>
            {p.location && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-white/5 text-slate-400">{p.location}</span>}
          </div>
          {p.target_search && <p className="text-xs text-slate-500 mt-0.5 truncate">captures: {p.target_search}</p>}
        </div>
        {p.drafting ? <Loader2 size={15} className="animate-spin text-emerald-400" /> : (
          <div className="flex items-center gap-2">
            <button onClick={() => setOpen(!open)} className="text-slate-400 hover:text-slate-200">{open ? <ChevronUp size={15} /> : <ChevronDown size={15} />}</button>
            <button data-testid={`discovery-del-${p.id}`} onClick={remove} className="text-slate-600 hover:text-red-400"><Trash2 size={13} /></button>
          </div>
        )}
      </div>
      {open && !p.drafting && (
        <div className="mt-3 space-y-3 text-sm">
          {p.intro && <p className="text-slate-300 leading-relaxed">{p.intro}</p>}
          {Array.isArray(p.sections) && p.sections.map((s, i) => (
            <div key={i} className="rounded-lg bg-[#0B0F17] border border-white/[0.06] p-2.5">
              <div className="text-[13px] font-semibold text-slate-100">{s.heading}</div>
              <p className="text-xs text-slate-400 mt-0.5">{s.body}</p>
            </div>
          ))}
          {Array.isArray(p.featured_members) && p.featured_members.length > 0 && (
            <div className="rounded-lg bg-purple-500/[0.06] border border-purple-500/20 p-2.5">
              <div className="text-[10px] font-bold uppercase tracking-wider text-purple-300 mb-1.5">Featured members (live profiles)</div>
              <div className="space-y-1.5">
                {p.featured_members.map((m, i) => (
                  <a key={i} data-testid={`featured-member-${i}`} href={m.profile_url} target="_blank" rel="noreferrer"
                    className="flex items-start gap-1.5 text-xs text-slate-300 hover:text-emerald-300 transition-colors group">
                    <ExternalLink size={11} className="mt-0.5 text-purple-300 group-hover:text-emerald-300 shrink-0" />
                    <span><span className="font-semibold">{m.name}</span>{m.business ? ` · ${m.business}` : ""}{m.why ? ` — ${m.why}` : ""}</span>
                  </a>
                ))}
              </div>
            </div>
          )}
          {Array.isArray(p.member_slots) && p.member_slots.length > 0 && (
            <div className="rounded-lg bg-white/[0.03] border border-white/[0.06] p-2.5">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Route to members</div>
              {p.member_slots.map((m, i) => <p key={i} className="text-xs text-slate-400">• {m}</p>)}
            </div>
          )}
          {p.cta && <p className="text-xs text-emerald-400 font-semibold">{p.cta}</p>}
          {p.why_traffic && <p className="text-[11px] text-slate-500">Why: {p.why_traffic}</p>}
        </div>
      )}
    </div>
  );
};

const CopyBtn = ({ text, testid }) => {
  const [done, setDone] = useState(false);
  const copy = async () => { try { await navigator.clipboard.writeText(text || ""); setDone(true); toast.success("Copied — paste into your Social Studio."); setTimeout(() => setDone(false), 1500); } catch { toast.error("Copy failed."); } };
  return (
    <button data-testid={testid} onClick={copy} className="flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-lg bg-white/[0.06] text-slate-200 hover:bg-white/[0.1] transition-all">
      {done ? <Check size={11} className="text-emerald-400" /> : <Copy size={11} />} {done ? "Copied" : "Copy"}
    </button>
  );
};

const SocialStudio = () => {
  const [plans, setPlans] = useState([]);
  const [source, setSource] = useState("");
  const [busy, setBusy] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [showConnect, setShowConnect] = useState(false);
  const [url, setUrl] = useState("");
  const [secret, setSecret] = useState("");

  const load = async () => setPlans(await api.socialPlans());
  const loadCfg = async () => { const c = await api.studioConfig(); setCfg(c); setUrl(c.social_studio_url || ""); };
  useEffect(() => { load(); loadCfg(); }, []);

  const saveCfg = async () => {
    const c = await api.updateStudioConfig({ social_studio_url: url.trim(), ...(secret.trim() ? { social_studio_secret: secret.trim() } : {}) });
    setCfg(c); setSecret(""); setShowConnect(false);
    toast.success(c.configured ? "Social Studio connected — one-click send is on." : "Saved.");
  };

  const generate = async () => {
    if (!source.trim()) { toast.error("Give KURT a topic, article or member to build from."); return; }
    setBusy(true);
    try {
      await api.socialPlan(source.trim());
      setSource("");
      toast.message("KURT is deciding the strategy…");
      let n = 0;
      const t = setInterval(async () => { n++; await load(); if (n > 15) { clearInterval(t); setBusy(false); } }, 4000);
    } catch { toast.error("Couldn't build the plan."); setBusy(false); }
  };

  return (
    <div>
      <p className="text-slate-400 text-sm mb-1">KURT decides <span className="text-slate-200">what to post, which member/article to amplify, the hook, caption & CTA — and why.</span> Copy it into your S/ME Social Studio to design + publish.</p>
      <p className="text-[11px] text-slate-500 mb-3">Instagram = brand, discovery & member acquisition · LinkedIn = founders & credibility. Every post feeds people back into the magazine.</p>

      <div className="flex items-center justify-between gap-2 rounded-lg border border-white/10 bg-[#121824] px-3 py-2 mb-4">
        <span className="text-[11px] flex items-center gap-1.5">
          <span className={`w-1.5 h-1.5 rounded-full ${cfg?.configured ? "bg-emerald-400" : "bg-slate-500"}`} />
          {cfg?.configured ? <span className="text-emerald-300">Social Studio connected — one-click send is on</span> : <span className="text-slate-400">One-click send not connected — you can still copy each post</span>}
        </span>
        <button data-testid="studio-connect-toggle" onClick={() => setShowConnect(!showConnect)} className="text-[11px] font-semibold text-slate-300 hover:text-slate-100">{showConnect ? "Close" : (cfg?.configured ? "Edit" : "Connect")}</button>
      </div>
      {showConnect && (
        <div className="rounded-lg border border-white/10 bg-[#0B0F17] p-3 mb-4 space-y-2">
          <p className="text-[11px] text-slate-400">Paste into your magazine's builder: <span className="text-slate-300">"Add POST /api/social-studio/ingest that accepts a social post JSON, protected by header X-Ingest-Secret from env SOCIAL_INGEST_SECRET."</span> Then enter its URL + secret here.</p>
          <input data-testid="studio-url-input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://smemagazine.co.uk/api/social-studio/ingest"
            className="w-full bg-[#121824] border border-white/10 rounded-lg px-2.5 py-1.5 text-sm outline-none focus:border-emerald-500/40" />
          <input data-testid="studio-secret-input" value={secret} onChange={(e) => setSecret(e.target.value)} placeholder="Shared secret (SOCIAL_INGEST_SECRET)"
            className="w-full bg-[#121824] border border-white/10 rounded-lg px-2.5 py-1.5 text-sm outline-none focus:border-emerald-500/40" />
          <button data-testid="studio-save-config" onClick={saveCfg} className="text-[11px] font-semibold px-3 py-1.5 rounded-lg bg-emerald-500 text-[#04140d] hover:bg-emerald-400 transition-all">Save connection</button>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-2 mb-5">
        <input data-testid="social-source-input" value={source} onChange={(e) => setSource(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && !busy && generate()}
          placeholder="A topic, a cover story, an article, or a member to amplify…"
          className="flex-1 bg-[#121824] border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-emerald-500/40" />
        <button data-testid="social-generate-btn" onClick={generate} disabled={busy}
          className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-500 text-[#04140d] font-semibold text-sm hover:bg-emerald-400 disabled:opacity-60 transition-all">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} Build social plan
        </button>
      </div>
      {plans.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-[#121824] p-8 text-center text-sm text-slate-400">No plans yet — give KURT something to turn into social.</div>
      ) : (
        <div className="space-y-3">{plans.map((p) => <SocialCard key={p.id} p={p} onChange={load} studioConnected={cfg?.configured} />)}</div>
      )}
    </div>
  );
};

const SocialCard = ({ p, onChange, studioConnected }) => {
  const [result, setResult] = useState(p.result || "");
  const [sending, setSending] = useState(false);
  const markPosted = async () => { await api.socialUpdate(p.id, { posted: true }); await onChange(); toast.success("Marked as posted."); };
  const saveResult = async () => { await api.socialUpdate(p.id, { result }); await onChange(); toast.success("Result saved — KURT will learn from it."); };
  const remove = async () => { await api.socialDelete(p.id); await onChange(); };
  const sendToStudio = async () => {
    setSending(true);
    try {
      const r = await api.socialSend(p.id);
      if (r.configured && r.sent) { toast.success("Sent to S/ME Social Studio."); await onChange(); }
      else if (!r.configured) { toast.message(r.message || "Connect Social Studio first (top of this tab)."); }
      else { toast.error(`Studio rejected it (${r.status}).`); }
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't reach Social Studio."); }
    finally { setSending(false); }
  };
  const ig = p.instagram_post || {}, st = p.instagram_story || {}, li = p.linkedin_post || {};

  return (
    <div data-testid={`social-plan-${p.id}`} className="rounded-2xl border border-white/10 bg-[#121824] p-4">
      <div className="flex items-start gap-2 mb-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-slate-100 truncate">{p.drafting ? "Deciding the strategy…" : (p.angle || p.source)}</span>
            {p.posted && <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300">Posted</span>}
          </div>
          <p className="text-[11px] text-slate-500 mt-0.5 truncate">{p.source}</p>
        </div>
        {p.drafting ? <Loader2 size={15} className="animate-spin text-emerald-400" /> :
          <button data-testid={`social-del-${p.id}`} onClick={remove} className="text-slate-600 hover:text-red-400 shrink-0"><Trash2 size={13} /></button>}
      </div>

      {!p.drafting && (
        <>
          {p.amplify && (p.amplify.member || p.amplify.article) && (
            <div className="rounded-lg bg-purple-500/[0.06] border border-purple-500/20 p-2.5 mb-3 text-xs">
              <span className="text-[10px] font-bold uppercase tracking-wider text-purple-300">Amplify · </span>
              {p.amplify.member && <a href={p.amplify.member_url || "#"} target="_blank" rel="noreferrer" className="text-slate-200 hover:text-emerald-300 font-semibold">{p.amplify.member}</a>}
              {p.amplify.article && <span className="text-slate-400"> → {p.amplify.article}</span>}
            </div>
          )}

          <div className="grid md:grid-cols-3 gap-3">
            {/* Instagram post */}
            <div className="rounded-xl bg-[#0B0F17] border border-white/[0.06] p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="flex items-center gap-1.5 text-[11px] font-bold text-rose-300"><Instagram size={12} /> IG Post</span>
                <CopyBtn testid={`copy-ig-${p.id}`} text={`${ig.caption || ""}\n\n${(ig.hashtags || []).join(" ")}`} />
              </div>
              {ig.hook && <p className="text-[12px] font-semibold text-slate-100 mb-1">{ig.hook}</p>}
              <p className="text-[11px] text-slate-400 whitespace-pre-line leading-relaxed">{ig.caption}</p>
              {ig.cta && <p className="text-[11px] text-emerald-400 mt-2">{ig.cta}</p>}
              {ig.hashtags?.length > 0 && <p className="text-[10px] text-blue-300/70 mt-1.5">{ig.hashtags.join(" ")}</p>}
              {ig.visual_idea && <p className="text-[10px] text-slate-500 mt-2 italic">🎨 {ig.visual_idea}</p>}
            </div>
            {/* IG Story */}
            <div className="rounded-xl bg-[#0B0F17] border border-white/[0.06] p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="flex items-center gap-1.5 text-[11px] font-bold text-amber-300"><Instagram size={12} /> IG Story</span>
                <CopyBtn testid={`copy-story-${p.id}`} text={`${st.text || ""}\n${st.cta || ""}`} />
              </div>
              <p className="text-[11px] text-slate-300 whitespace-pre-line leading-relaxed">{st.text}</p>
              {st.cta && <p className="text-[11px] text-emerald-400 mt-2">{st.cta}</p>}
              {st.visual_idea && <p className="text-[10px] text-slate-500 mt-2 italic">🎨 {st.visual_idea}</p>}
            </div>
            {/* LinkedIn */}
            <div className="rounded-xl bg-[#0B0F17] border border-white/[0.06] p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="flex items-center gap-1.5 text-[11px] font-bold text-blue-300"><Linkedin size={12} /> LinkedIn</span>
                <CopyBtn testid={`copy-li-${p.id}`} text={`${li.hook || ""}\n\n${li.body || ""}\n\n${li.cta || ""}`} />
              </div>
              {li.hook && <p className="text-[12px] font-semibold text-slate-100 mb-1">{li.hook}</p>}
              <p className="text-[11px] text-slate-400 whitespace-pre-line leading-relaxed">{li.body}</p>
              {li.cta && <p className="text-[11px] text-emerald-400 mt-2">{li.cta}</p>}
            </div>
          </div>

          {p.why && <div className="mt-3 rounded-lg bg-emerald-500/[0.05] border border-emerald-500/20 p-2.5"><span className="text-[10px] font-bold uppercase tracking-wider text-emerald-400">Why · </span><span className="text-[11px] text-slate-300">{p.why}</span></div>}

          <div className="flex flex-wrap items-center gap-2 mt-3">
            <button data-testid={`social-send-${p.id}`} onClick={sendToStudio} disabled={sending}
              className={`flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-lg transition-all disabled:opacity-50 ${studioConnected ? "bg-emerald-500 text-[#04140d] hover:bg-emerald-400" : "bg-white/[0.06] text-slate-300 hover:bg-white/[0.1]"}`}
              title={studioConnected ? "Send straight to your Social Studio" : "Connect Social Studio to enable"}>
              {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Send to Studio
            </button>
            {p.sent_to_studio && <span className="text-[10px] text-emerald-400 flex items-center gap-1"><Check size={11} /> sent</span>}
            {!p.posted && (
              <button data-testid={`social-posted-${p.id}`} onClick={markPosted} className="flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-lg bg-white/[0.06] text-slate-100 hover:bg-white/[0.1] transition-all"><Check size={12} className="text-emerald-400" /> Mark posted</button>
            )}
            <input data-testid={`social-result-${p.id}`} value={result} onChange={(e) => setResult(e.target.value)} placeholder="Result (e.g. 4.2k reach, 60 profile visits)…"
              className="flex-1 min-w-[160px] bg-[#0B0F17] border border-white/10 rounded-lg px-2.5 py-1.5 text-[11px] outline-none focus:border-emerald-500/40" />
            <button data-testid={`social-save-result-${p.id}`} onClick={saveResult} className="text-[11px] font-semibold px-2.5 py-1.5 rounded-lg bg-emerald-500 text-[#04140d] hover:bg-emerald-400 transition-all">Save result</button>
          </div>
        </>
      )}
    </div>
  );
};

const WhatsWorking = () => {
  const [a, setA] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => setA(await api.trafficAnalysis());
  useEffect(() => { load(); }, []);
  const run = async () => {
    setBusy(true);
    try {
      await api.trafficAnalyze();
      toast.message("KURT is analysing what's working…");
      let n = 0;
      const t = setInterval(async () => { n++; const d = await api.trafficAnalysis(); setA(d); if ((d && d.analyzed_at && !d.drafting) || n > 15) { clearInterval(t); setBusy(false); } }, 4000);
    } catch { toast.error("Analysis failed."); setBusy(false); }
  };
  return (
    <div>
      <p className="text-slate-400 text-sm mb-4">KURT reads what's gaining traffic, explains why, and recommends how to compound it. <span className="text-amber-400">Illustrative until GA4 / Search Console are connected.</span></p>
      <button data-testid="whats-working-btn" onClick={run} disabled={busy}
        className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-500 text-[#04140d] font-semibold text-sm hover:bg-emerald-400 disabled:opacity-60 transition-all mb-5">
        {busy ? <Loader2 size={16} className="animate-spin" /> : <Activity size={16} />} Analyse what's working
      </button>
      {a && a.summary && (
        <div className="space-y-4">
          <div className="rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.05] p-4"><p className="text-sm text-slate-200">{a.summary}</p></div>
          {Array.isArray(a.top_movers) && a.top_movers.length > 0 && (
            <div>
              <h3 className="kz-head text-sm font-bold mb-2">Top movers</h3>
              <div className="space-y-2">{a.top_movers.map((m, i) => (
                <div key={i} className="rounded-xl border border-white/10 bg-[#121824] p-3">
                  <div className="text-sm font-semibold text-slate-100">{m.title}</div>
                  <p className="text-xs text-slate-400 mt-0.5">{m.why}</p>
                </div>
              ))}</div>
            </div>
          )}
          {Array.isArray(a.recommendations) && a.recommendations.length > 0 && (
            <div>
              <h3 className="kz-head text-sm font-bold mb-2">Do more of this</h3>
              <div className="space-y-2">{a.recommendations.map((r, i) => (
                <div key={i} className="rounded-xl border border-white/10 bg-[#121824] p-3 flex items-start gap-2">
                  <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-white/5 text-slate-400 shrink-0 mt-0.5">{(r.type || "").replace(/_/g, " ")}</span>
                  <div>
                    <div className="text-sm font-semibold text-slate-100">{r.move}</div>
                    <p className="text-xs text-slate-400 mt-0.5">{r.reason}</p>
                  </div>
                </div>
              ))}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
